/**
 * Analytics › Money (brands that sell something or give access): members now, gained and lost,
 * rentals and purchases with an estimate at store prices before store fees, and the access given
 * without a purchase. From the store purchases and grants the backend already holds.
 */

import { SkeletonCard } from "@web-ui/components/Skeleton";
import { TileGrid } from "../components/tile-grid";
import { productLabel } from "@/forms/access";
import { analytics, type MoneyReport, type SaleRow } from "../api";
import { LineChart } from "../charts/line-chart";
import type { Table } from "../csv";
import { definition, type MetricId } from "../definitions";
import { change, count, dayLabel, estimatedTotal, NONE } from "../format";
import { alignToDays, eachDay } from "../range";
import { MetricTile } from "../components/metric-tile";
import { ReportFailed } from "../components/no-data";
import { ReportCard } from "../components/report-card";
import { SampleNotice } from "../components/sample-notice";
import type { ReportScope } from "../scope";
import { useReport } from "../use-report";

export const ESTIMATE_NOTE = "Estimated, at store prices before store fees.";

const KINDS: Record<NonNullable<SaleRow["kind"]>, string> = { subscription: "Membership", rental: "Rental", purchase: "Purchase" };
const sales = (m: MoneyReport) => m.sales.filter((s) => s.kind !== "subscription");

export function Money({ scope }: { scope: ReportScope }) {
  const now = useReport(() => analytics.money(scope.client, scope.range), [scope.client, scope.range]);
  const before = useReport(() => analytics.money(scope.client, scope.previous), [scope.client, scope.previous]);
  const m = now.data;
  const d = (id: MetricId) => definition(id, scope.pack);

  if (now.failed && !m) return <section className="card"><ReportFailed /></section>;
  if (!m) return <SkeletonCard label="Loading sales and members" rows={6} />;
  const b = before.data;
  const sold = sales(m).reduce((n, s) => n + s.count, 0);
  const soldBefore = b ? sales(b).reduce((n, s) => n + s.count, 0) : null;
  const estimate = estimatedTotal(sales(m));

  return (
    <div className={`flex flex-col gap-3 transition-opacity ${now.stale ? "opacity-60" : ""}`} aria-busy={now.stale || undefined}>
      <SampleNotice shown={m.sample || !!b?.sample} />
      <TileGrid columns={3}>
        <MetricTile definition={d("subscribers")} value={count(m.subscribers.active)} />
        <MetricTile definition={d("started")} value={count(m.subscribers.started)} change={change(m.subscribers.started, b?.subscribers.started)} trend={alignToDays(m.series, scope.range, (p) => p.started)} />
        <MetricTile definition={d("ended")} value={count(m.subscribers.ended)} change={change(m.subscribers.ended, b?.subscribers.ended)} upIsGood={false} trend={alignToDays(m.series, scope.range, (p) => p.ended)} />
        <MetricTile definition={d("sales")} value={count(sold)} change={change(sold, soldBefore)} trend={alignToDays(m.series, scope.range, (p) => p.sales)} />
        <MetricTile definition={d("estimatedSales")} value={estimate ?? NONE} />
        <MetricTile definition={d("comps")} value={count(m.comps.granted)} change={change(m.comps.granted, b?.comps.granted)} />
      </TileGrid>
      <p className="text-caption text-t-secondary ps-card-inset">{ESTIMATE_NOTE} {d("codesRedeemed").label}: {count(m.comps.codesRedeemed)}.</p>
      <div className="grid items-start gap-3 xl:grid-cols-2">
        <DailyCard scope={scope} m={m} metric="started" />
        <DailyCard scope={scope} m={m} metric="ended" />
      </div>
      <SalesCard scope={scope} m={m} />
    </div>
  );
}

/** One daily series as its own small chart (members gained and lost are never two scales on one plot). */
function DailyCard({ scope, m, metric }: { scope: ReportScope; m: MoneyReport; metric: "started" | "ended" }) {
  const def = definition(metric, scope.pack);
  const days = eachDay(scope.range);
  const values = alignToDays(m.series, scope.range, (p) => p[metric]);
  const table: Table<{ day: string; value: number }> = {
    columns: [
      { label: "Day", value: (r) => r.day, format: (_, r) => dayLabel(r.day) },
      { label: def.label, value: (r) => r.value, format: (v) => count(Number(v)), numeric: true },
    ],
    rows: days.map((day, i) => ({ day, value: values[i]! })),
  };
  const total = values.reduce((a, v) => a + v, 0);
  return (
    <ReportCard title={def} description="Per day, in this period." table={table} file={{ brand: scope.brand, what: def.label, range: scope.range }}>
      <LineChart title={`${def.label} per day`} description={`${count(total)} ${def.label.toLowerCase()} from ${dayLabel(scope.range.from)} to ${dayLabel(scope.range.to)}.`}
        labels={days.map(dayLabel)} series={[{ id: metric, label: def.label, values, kind: "current" }]} format={count} height={180} />
    </ReportCard>
  );
}

function SalesCard({ scope, m }: { scope: ReportScope; m: MoneyReport }) {
  const table: Table<SaleRow> = {
    columns: [
      { label: "What", value: (s) => (s.kind ? productLabel({ ...s, kind: s.kind, price: null }, scope.brand) : s.productId) },
      { label: "Product id", value: (s) => s.productId, csvOnly: true },
      { label: "Kind", value: (s) => (s.kind ? KINDS[s.kind] : "Other") },
      { label: "Price", value: (s) => s.price, format: (v) => (v === null ? NONE : String(v)) },
      { label: "Sold", tooltip: "Purchases, rentals or new memberships in this period.", value: (s) => s.count, format: (v) => count(Number(v)), numeric: true },
      { label: "Estimated", tooltip: ESTIMATE_NOTE, value: (s) => estimatedTotal([s]), format: (v) => (v === null ? NONE : String(v)), numeric: true },
    ],
    rows: [...m.sales].sort((a, b) => b.count - a.count),
  };
  return (
    <ReportCard tableOnly title="By product" description={`Each thing you sell, in this period. ${ESTIMATE_NOTE}`} table={table}
      file={{ brand: scope.brand, what: "sales", range: scope.range }}
      empty={m.sales.length === 0 ? <p className="text-body-2 text-t-secondary ps-card-inset pb-2">Nothing was sold in this period.</p> : undefined} />
  );
}
